import { useState } from 'preact/hooks'
import { useLocation, useRoute } from 'wouter-preact'
import { Screen } from '~/components/layout/Screen'
import { Button } from '~/components/primitives/Button'
import { InfoButton } from '~/components/primitives/InfoButton'
import { useAsyncData } from '~/hooks/useAsyncData'
import {
  type ChangeableDomainField,
  DOMAIN_INTENTION_LABELS,
  DOMAIN_PRIORITY_LABELS,
  type DomainGoalChange,
  type DomainGoalRecord,
  FITNESS_DOMAIN_LABELS,
  type FitnessDomain,
  isPlanned,
  type PlannedItem,
  type WeeklyCommitment,
} from '~/models/fitnessDomain'
import { PHASE_LABELS } from '~/models/phase'
import {
  ANCHOR_COPY,
  ROUTINE_ANCHOR_LABELS,
  type RoutineAnchorRecord,
} from '~/models/routineAnchor'
import { DEFAULT_WEEK_START_DAY } from '~/models/settings'
import { sessionsInWeekOrder, standardWeekFor } from '~/models/trainingSchedule'
import { WEEKDAY_LABELS, type Weekday } from '~/models/weekday'
import { PLAN_FROM_PROPOSAL_ROUTE, PLAN_ROUTE } from '~/routes'
import { DOMAIN_REGISTRY } from '~/rules/domainRegistry'
import { suggestedCommitment } from '~/rules/plan/suggestedCommitment'
import {
  endDomainGoal,
  nextCommitmentEffectiveDate,
  pauseDomainGoal,
  resumeDomainGoal,
} from '~/services/domainPlanService'
import { getEffectiveWeekStartDay } from '~/services/weekBoundaryService'
import { useDatabase } from '~/storage/DatabaseContext'
import type { SolaDatabase } from '~/storage/db'
import { listAiProposals } from '~/storage/repositories/aiProposalRepository'
import { listDomainGoals } from '~/storage/repositories/domainGoalRepository'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { listRoutineAnchors } from '~/storage/repositories/routineAnchorRepository'
import { STORE } from '~/storage/schema'
import type { CalendarDate } from '~/types/dates'
import { unwrapTime } from '~/types/dates'
import type { DomainGoalId } from '~/types/ids'
import { formatCalendarDate } from '~/utils/format'
import { CommitmentEditor } from './CommitmentEditor'
import styles from './PlanPage.module.css'
import { ScheduleEditor } from './ScheduleEditor'

/**
 * My Plan (D-090, D-099).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE PLAN LIVES HERE SO THAT TODAY DOES NOT HAVE TO CARRY IT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Today is the execution surface: one dominant action, a stable layout, nothing
 * competing with it. Everything a user might want to *check* rather than *do* —
 * why this plan, what each area is for, what changed — belongs on a screen they
 * arrive at deliberately. Folding it into a disclosure on Today would make the
 * screen's height depend on how many areas they chose, which is exactly the
 * instability a routine anchor cannot survive.
 *
 * **This screen does not duplicate Settings.** Credentials, reminders, privacy,
 * backup and technical controls stay there; the plan links to them and owns none
 * of them.
 *
 * **What it deliberately does not show:** any score, grade, percentage, streak or
 * completion bar. The anti-scoring rule is not limited to the Today screen, and a
 * plan screen is exactly where a progress meter would feel natural and be wrong.
 */
export function PlanPage() {
  const [, navigate] = useLocation()
  const db = useDatabase()
  const [editing, setEditing] = useState<DomainGoalId | undefined>(undefined)
  /* Separate from `editing` rather than one "which editor is open" value, because
     a proposal opens the commitment editor by id (D-093 §6) and merging the two
     would let a suggestion open the schedule editor instead. */
  const [scheduling, setScheduling] = useState<DomainGoalId | undefined>(undefined)

  /**
   * The proposal this screen was opened from, if it was (D-093 §6).
   *
   * **Named by id in the path and re-read from storage, never carried in the
   * URL.** The numbers a user is about to save come from the record the app kept
   * at phase 4, not from a string anything could have written.
   */
  const [matched, params] = useRoute(PLAN_FROM_PROPOSAL_ROUTE)
  const proposalId = matched ? params.proposalId : undefined

  const { state, reload } = useAsyncData(async () => {
    const project = await getActiveProject(db)
    if (!project.ok || project.value === undefined) {
      return {
        project: undefined,
        goals: [] as readonly DomainGoalRecord[],
        phase: undefined,
        effectiveFrom: undefined,
        anchors: [] as readonly RoutineAnchorRecord[],
        weekStartDay: DEFAULT_WEEK_START_DAY,
        prefills: {} as SuggestedByDomain,
      }
    }

    const [goals, phase, effectiveFrom, anchors, weekStartDay] = await Promise.all([
      listDomainGoals(db, project.value.id),
      getActivePhase(db),
      nextCommitmentEffectiveDate(db, new Date()),
      listRoutineAnchors(db),
      /* The order the week runs in, from the value in force rather than the one
         newly chosen (D-105 §8). While a change is pending this screen keeps the
         current order, and the future order is not previewed. */
      getEffectiveWeekStartDay(db, new Date()),
    ])
    return {
      project: project.value,
      goals: goals.ok ? goals.value : [],
      phase: phase.ok ? phase.value : undefined,
      effectiveFrom: effectiveFrom.ok ? effectiveFrom.value : undefined,
      anchors: anchors.ok ? anchors.value : [],
      weekStartDay: weekStartDay.ok ? weekStartDay.value : DEFAULT_WEEK_START_DAY,
      prefills: await readPrefills(db, proposalId),
    }
  }, [
    STORE.projects,
    STORE.domainGoals,
    STORE.phases,
    STORE.settings,
    STORE.routineAnchors,
    /* Deliberately NOT `STORE.aiProposals`. A proposal is immutable once written,
       so nothing can change the values on screen — and re-reading on every write
       would reopen this editor the moment another proposal was stored elsewhere. */
  ])

  const back = (
    <Button variant="secondary" onClick={() => navigate('/today')}>
      Back to Today
    </Button>
  )

  if (state.status === 'loading') {
    return (
      <Screen title="My plan">
        <p>Loading…</p>
      </Screen>
    )
  }

  if (state.status === 'error') {
    return (
      <Screen title="My plan">
        <p role="alert">Project Sola could not read your plan. {state.message}</p>
        {back}
      </Screen>
    )
  }

  const { project, goals, phase, effectiveFrom, anchors, weekStartDay, prefills } = state.value

  if (project === undefined) {
    return (
      <Screen title="My plan">
        <p class={styles.lede}>There is no project running, so there is no plan yet.</p>
        {back}
      </Screen>
    )
  }

  const live = goals.filter((goal) => goal.status !== 'ended')
  const primary = live.find((goal) => goal.priority === 'primary')
  const supporting = live.filter((goal) => goal.priority === 'supporting')
  const protectedGoals = live.filter((goal) => goal.priority === 'protect')
  const other = live.filter((goal) => goal.priority === 'none')

  /* The goal a suggestion is for, opened without the user hunting for it — the
     point of D-093 §6 is that accepting a proposal *lands* in the editor. Derived
     rather than pushed into state by an effect, so the data arriving cannot race
     the state that depends on it. */
  const suggested = live.find((goal) => prefills[goal.domain] !== undefined)
  const openEditor = editing ?? suggested?.id

  const groupProps = {
    editing: openEditor,
    scheduling,
    weekStartDay,
    onSchedule: setScheduling,
    effectiveFrom,
    prefills,
    onEdit: setEditing,
    onDone: () => {
      setEditing(undefined)
      setScheduling(undefined)
      /* Drop the proposal from the URL once it has been dealt with. Staying on
         `/plan/from/:id` would reopen the editor on the next reload with numbers
         the user has already accepted or declined. */
      if (proposalId !== undefined) navigate(PLAN_ROUTE, { replace: true })
      reload()
    },
  }

  return (
    <Screen title="My plan">
      <p class={styles.lede}>
        {project.name} · to {formatCalendarDate(project.targetDate)}
      </p>

      {primary === undefined ? null : (
        <GoalGroup heading="Main focus" goals={[primary]} {...groupProps} />
      )}
      {supporting.length === 0 ? null : (
        <GoalGroup heading="Supporting" goals={supporting} {...groupProps} />
      )}
      {protectedGoals.length === 0 ? null : (
        <GoalGroup heading="Protecting" goals={protectedGoals} {...groupProps} />
      )}
      {other.length === 0 ? null : (
        <GoalGroup heading="Also tracking" goals={other} {...groupProps} />
      )}

      {phase === undefined ? null : (
        <section class={styles.section}>
          <h2 class={styles.heading}>Body-composition phase</h2>
          <p class={styles.body}>
            <strong>{PHASE_LABELS[phase.type]}</strong>, aiming for{' '}
            {phase.targetWeeklyChangeMinPercent}% to {phase.targetWeeklyChangeMaxPercent}% a week.
          </p>
          <p class={styles.body}>
            Planned to {formatCalendarDate(phase.plannedEndDate)}. Reaching that date changes
            nothing on its own — Project Sola will tell you, and the decision stays yours.
          </p>
        </section>
      )}

      {project.recommendationSnapshot === undefined ? null : (
        <section class={styles.section}>
          <h2 class={styles.heading}>Why this plan</h2>
          {/* The immutable snapshot taken when the project was confirmed (D-046).
              Read back rather than recomputed: a later edit to the phase must not
              rewrite the reasoning the user actually agreed to. */}
          <ul class={styles.reasons}>
            {project.recommendationSnapshot.reasoning.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </section>
      )}

      {/* Read-only, and set in Settings. An anchor is a preference rather than a
          commitment — no rule reads one and a missed one writes nothing — so it
          does not belong in the plan's own history. It is shown here because
          seeing *when* you meant to do something is useful beside *what* you
          meant to do. */}
      <RoutineTimes anchors={anchors} />

      <section class={styles.section}>
        <h2 class={styles.heading}>What changed</h2>
        <RecentChanges goals={live} />
      </section>

      <div class={styles.actions}>{back}</div>
    </Screen>
  )
}

interface GoalGroupProps {
  heading: string
  goals: readonly DomainGoalRecord[]
  editing: DomainGoalId | undefined
  scheduling: DomainGoalId | undefined
  weekStartDay: Weekday
  effectiveFrom: CalendarDate | undefined
  prefills: SuggestedByDomain
  onEdit: (id: DomainGoalId | undefined) => void
  onSchedule: (id: DomainGoalId | undefined) => void
  onDone: () => void
}

function GoalGroup({
  heading,
  goals,
  editing,
  scheduling,
  weekStartDay,
  effectiveFrom,
  prefills,
  onEdit,
  onSchedule,
  onDone,
}: GoalGroupProps) {
  return (
    <section class={styles.section}>
      <h2 class={styles.heading}>{heading}</h2>
      <ul class={styles.goalList}>
        {goals.map((goal) => (
          <li key={goal.id} class={styles.goal}>
            <p class={styles.goalName}>{FITNESS_DOMAIN_LABELS[goal.domain]}</p>
            <p class={styles.goalMeta}>
              {DOMAIN_INTENTION_LABELS[goal.intention]} · {DOMAIN_PRIORITY_LABELS[goal.priority]}
              {goal.status === 'paused' ? ' · Paused' : ''}
            </p>

            {/* An honest baseline state rather than a target nobody derived. */}
            {goal.baseline === 'baselineRequired' ? (
              <p class={styles.goalNote}>
                Project Sola needs a baseline before it can set a target here.
              </p>
            ) : null}

            {/* Named, not omitted. Silence about an area the user chose reads as
                approval — see `domainRegistry.ts`. */}
            {isPlanned(goal) && !DOMAIN_REGISTRY[goal.domain].implemented ? (
              <p class={styles.goalNote}>{DOMAIN_REGISTRY[goal.domain].reason}</p>
            ) : null}

            {scheduling === goal.id ? (
              <ScheduleEditor
                goal={goal}
                weekStartDay={weekStartDay}
                effectiveFrom={effectiveFrom}
                onSaved={onDone}
                onCancel={onDone}
              />
            ) : editing === goal.id ? (
              <CommitmentEditor
                goal={goal}
                effectiveFrom={effectiveFrom}
                prefill={prefills[goal.domain]}
                onSaved={onDone}
                /* Cancelling is dealt-with too: `onDone` clears the proposal from
                   the URL, so declining a suggestion does not leave it waiting to
                   reopen. It does reload, which is a cheap read. */
                onCancel={onDone}
              />
            ) : (
              <GoalPlan
                goal={goal}
                weekStartDay={weekStartDay}
                onEdit={() => onEdit(goal.id)}
                onSchedule={() => onSchedule(goal.id)}
                onChanged={onDone}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * What this area's week contains, and the controls that change it.
 *
 * `trackOnly` gets no plan and no editor, because track-only means "collect the
 * evidence and show it to me, and do not plan anything". Offering it a weekly
 * commitment would generate exactly the work the intention exists to decline.
 */
function GoalPlan({
  goal,
  weekStartDay,
  onEdit,
  onSchedule,
  onChanged,
}: {
  goal: DomainGoalRecord
  weekStartDay: Weekday
  onEdit: () => void
  onSchedule: () => void
  onChanged: () => void
}) {
  const db = useDatabase()
  const [busy, setBusy] = useState(false)
  const [confirmingEnd, setConfirmingEnd] = useState(false)

  const run = async (action: Promise<{ ok: boolean }>): Promise<void> => {
    setBusy(true)
    await action
    setBusy(false)
    onChanged()
  }

  const label = FITNESS_DOMAIN_LABELS[goal.domain].toLowerCase()
  /* Through the one reader (D-105 §3). Reading `weeklyCommitment.standard`
     directly would show an empty full week for a goal whose schedule superseded
     it, which is the two-authority problem appearing as a blank screen. */
  const standard = standardWeekFor(goal, weekStartDay)
  const sessions = sessionsInWeekOrder(goal.weeklySchedule ?? [], weekStartDay)

  return (
    <div class={styles.goalPlan}>
      {goal.intention === 'trackOnly' ? (
        <p class={styles.goalNote}>
          Tracked, not planned. Project Sola collects the evidence and asks nothing of you here.
        </p>
      ) : (
        <>
          {/*
            **This control adds information rather than hiding it** (D-104 §5).
            The three variants were three labels and nothing else — "a
            bare-minimum week" says what it is called and not what it is for, and
            `minimumViable` is the safety valve that makes a bad week survivable
            rather than a target to aim at. That distinction was written down in
            D-101 and had never reached a screen.
          */}
          <div class={styles.planHead}>
            <h3 class={styles.planHeading}>Weekly plan</h3>
            <InfoButton topic="the three weeks" title="Why there are three weeks">
              <p>
                <strong>A full week</strong> is what you intend to do when things go normally.
              </p>
              <p>
                <strong>A short week</strong> is the same plan with less in it, for a week you can
                see coming — travel, a heavy stretch at work.
              </p>
              <p>
                <strong>A bare-minimum week</strong> is the floor that keeps the habit alive when
                the week goes wrong. It is not a target, and Project Sola never sets it for you.
              </p>
            </InfoButton>
          </div>
          <PlanVariants commitment={goal.weeklyCommitment} standard={standard} />
          <Button
            variant="secondary"
            onClick={onEdit}
            label={`Change the weekly plan for ${label}`}
          >
            Change the weekly plan
          </Button>

          {/* The recurring week, and the one control that changes it (D-105 §7).
              A goal with a schedule derives its full week from it, so the two
              controls edit one plan from two angles rather than two plans. */}
          <h3 class={styles.planHeading}>Weekly schedule</h3>
          {sessions.length === 0 ? (
            <p class={styles.goalNote}>
              No sessions scheduled yet. Adding them is what puts training on Today.
            </p>
          ) : (
            <ul class={styles.scheduleSummary}>
              {sessions.map((session) => (
                <li key={session.id}>
                  {WEEKDAY_LABELS[session.weekday]} · {session.name}
                  {session.expectedMinutes === undefined ? '' : ` · ${session.expectedMinutes} min`}
                </li>
              ))}
            </ul>
          )}
          <Button
            variant="secondary"
            onClick={onSchedule}
            label={`Change the weekly schedule for ${label}`}
          >
            Change the weekly schedule
          </Button>
        </>
      )}

      <div class={styles.goalActions}>
        {goal.status === 'paused' ? (
          <Button
            variant="secondary"
            busy={busy}
            onClick={() => void run(resumeDomainGoal(db, goal.id, new Date()))}
            label={`Resume ${label}`}
          >
            Resume this area
          </Button>
        ) : (
          <Button
            variant="secondary"
            busy={busy}
            onClick={() => void run(pauseDomainGoal(db, goal.id, new Date()))}
            label={`Pause ${label}`}
          >
            Pause this area
          </Button>
        )}

        {confirmingEnd ? (
          <>
            {/* Ending is irreversible — the model refuses a transition out of
                `ended`, because reopening would be a new goal with its own start
                date. So it is confirmed rather than merely tapped. */}
            <p class={styles.goalNote}>
              Ending {label} keeps everything already recorded and cannot be undone. Starting it
              again later begins a new area with its own start date.
            </p>
            <Button
              busy={busy}
              onClick={() => void run(endDomainGoal(db, goal.id, new Date()))}
              label={`Confirm ending ${label}`}
            >
              Yes, end it
            </Button>
            <Button variant="secondary" onClick={() => setConfirmingEnd(false)}>
              Keep it
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={() => setConfirmingEnd(true)} label={`End ${label}`}>
            End this area
          </Button>
        )}
      </div>
    </div>
  )
}

const VARIANT_LABELS: readonly { key: keyof WeeklyCommitment; label: string }[] = [
  { key: 'standard', label: 'A full week' },
  { key: 'short', label: 'A short week' },
  { key: 'minimumViable', label: 'A bare-minimum week' },
]

function describeItem(item: PlannedItem): string {
  const times = item.timesPerWeek === 1 ? 'once' : `${item.timesPerWeek}×`
  return item.minutes === undefined
    ? `${item.label}, ${times}`
    : `${item.label}, ${times} · ${item.minutes} min`
}

/**
 * The three weeks.
 *
 * `standard` arrives separately because it is **derived** for a goal with a
 * schedule (D-105 §3) — the stored value was superseded and reading it here
 * would show an empty full week beside a schedule full of sessions.
 */
function PlanVariants({
  commitment,
  standard,
}: {
  commitment: WeeklyCommitment | undefined
  standard: readonly PlannedItem[]
}) {
  if (commitment === undefined) {
    return (
      <p class={styles.goalNote}>
        No weekly plan yet. Adding one tells Project Sola what to look for in a week — it never
        invents one for you.
      </p>
    )
  }

  return (
    <dl class={styles.variantList}>
      {VARIANT_LABELS.map(({ key, label }) => {
        const items = key === 'standard' ? standard : commitment[key]
        return (
          <div key={key} class={styles.variantRow}>
            <dt class={styles.variantName}>{label}</dt>
            <dd class={styles.variantItems}>
              {items.length === 0 ? 'Nothing planned' : items.map(describeItem).join(' · ')}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

/**
 * The routine times, read-only.
 *
 * Absent entirely when nothing is configured. A heading over an empty list would
 * be an instruction to go and configure something, and My Plan is not where that
 * happens — Settings is, and the Reminders group says so.
 */
function RoutineTimes({ anchors }: { anchors: readonly RoutineAnchorRecord[] }) {
  const set = anchors.filter((anchor) => anchor.enabled === 'yes' && anchor.startTime !== undefined)
  if (set.length === 0) return null

  return (
    <section class={styles.section}>
      <h2 class={styles.heading}>{ANCHOR_COPY.editorHeading}</h2>
      <dl class={styles.variantList}>
        {set.map((anchor) => (
          <div key={anchor.kind} class={styles.variantRow}>
            <dt class={styles.variantName}>{ROUTINE_ANCHOR_LABELS[anchor.kind]}</dt>
            <dd class={styles.variantItems}>
              {anchor.startTime === undefined ? '' : unwrapTime(anchor.startTime)}
            </dd>
          </div>
        ))}
      </dl>
      <p class={styles.goalNote}>{ANCHOR_COPY.cannotNotify} Change them in Settings.</p>
    </section>
  )
}

const CHANGE_FIELD_LABELS: Readonly<Record<ChangeableDomainField, string>> = {
  status: 'Status',
  priority: 'Priority',
  intention: 'Intention',
  direction: 'Direction',
  reassessOn: 'Reassessment date',
  weeklyCommitment: 'Weekly plan',
  /* Named apart from the weekly plan on purpose: the history reads better when
     superseding a hand-written standard week and editing the schedule that
     superseded it are two lines rather than one ambiguous one (D-105 §3). */
  weeklySchedule: 'Training schedule',
}

/**
 * One change, in words.
 *
 * **A `weeklyCommitment` entry has no `from`/`to` and must not pretend to.** The
 * earlier version of this component interpolated both unconditionally, which for
 * a commitment change would have rendered "changed from undefined to undefined".
 * A commitment is structured data; what a finished week was actually planned to
 * contain lives in that week's review snapshot, not in this list.
 */
function describeChange(change: DomainGoalChange): string {
  const field = CHANGE_FIELD_LABELS[change.field]
  const when =
    change.effectiveFrom === undefined ? '' : `, from ${formatCalendarDate(change.effectiveFrom)}`

  if (change.from === undefined || change.to === undefined) {
    return `${field} changed${when}`
  }
  return `${field} changed from ${change.from} to ${change.to}${when}`
}

/**
 * The most recent changes, newest first.
 *
 * Read from each goal's append-only `changes` list, which is what makes "changing
 * a goal preserves history" visible rather than merely true.
 */
function RecentChanges({ goals }: { goals: readonly DomainGoalRecord[] }) {
  const changes = goals
    .flatMap((goal) => goal.changes.map((change) => ({ goal, change })))
    .sort((left, right) => right.change.changedAt.localeCompare(left.change.changedAt))
    .slice(0, 5)

  if (changes.length === 0) {
    return <p class={styles.body}>Nothing has changed since this project started.</p>
  }

  return (
    <ul class={styles.changeList}>
      {changes.map(({ goal, change }) => (
        <li key={`${goal.id}-${change.field}-${change.changedAt}`} class={styles.change}>
          {FITNESS_DOMAIN_LABELS[goal.domain]}: {describeChange(change)}
          {change.reason === undefined ? '' : ` — ${change.reason}`}
        </li>
      ))}
    </ul>
  )
}

/**
 * The commitments a stored proposal suggests, keyed by domain (D-093 §6).
 *
 * Empty for every ordinary visit, and empty rather than an error when the id
 * names nothing: a stale link should open My Plan, not a failure state, because
 * the screen is perfectly useful without the suggestion.
 *
 * **Only `weeklyCommitment` suggestions exist and only they may pre-fill
 * anything.** The phase type, the target rate, the route and every rules-engine
 * input are not pre-fillable and never become so — that is D-062, and a pre-fill
 * is the exact shape route input would take if it ever arrived. The type system
 * carries that here: `GeminiSuggestion` is a union of one.
 */
type SuggestedByDomain = Readonly<Partial<Record<FitnessDomain, WeeklyCommitment>>>

async function readPrefills(
  db: SolaDatabase,
  proposalId: string | undefined,
): Promise<SuggestedByDomain> {
  if (proposalId === undefined) return {}

  const listed = await listAiProposals(db)
  if (!listed.ok) return {}

  const proposal = listed.value.find((entry) => entry.id === proposalId)
  if (proposal === undefined) return {}

  const prefills: Partial<Record<FitnessDomain, WeeklyCommitment>> = {}
  for (const suggestion of proposal.suggestions) {
    const derived = suggestedCommitment({
      domain: suggestion.domain,
      sessionsPerWeek: suggestion.sessionsPerWeek,
      minutesPerSession: suggestion.minutesPerSession,
    })
    if (derived !== undefined) prefills[derived.domain] = derived.commitment
  }
  return prefills
}
